import { useMemo, useState, useCallback } from "react";
import { useSnackbar } from "notistack";

import { useIoCContext } from "@context/IoCContext/IoCContext";

import {
  INotificationService,
  IUserNotificationWithDetails,
} from "@modules/notifications/model/INotifications";

import { Types } from "@ioc/types";

import { IHookNotificationPortal } from "../model/inteface.notificationPortal";

const useNotificationPortalInternal = (): IHookNotificationPortal => {
  const iocContext = useIoCContext();
  const { enqueueSnackbar } = useSnackbar();

  const service = useMemo(
    () =>
      iocContext.serviceContainer.get<INotificationService>(
        Types.Notifications.INotificationService
      ),
    [iocContext.serviceContainer]
  );

  const [open, setOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<
    IUserNotificationWithDetails[]
  >([]);
  const [
    currentNotification,
    setCurrentNotification,
  ] = useState<IUserNotificationWithDetails | null>(null);

  const handleClose = () => {
    setCurrentNotification(null);
    setOpen(false);
  };

  const hasNextNotification = useMemo(
    () =>
      notifications.some(
        (item) => !item.readAt && item.id !== currentNotification?.id
      ),
    [notifications, currentNotification?.id]
  );

  const reopen = (uuid) => {
    // só reabre se ainda existir alguma notificação carregada
    if (notifications.length > 0) {
      const find = notifications.find((item) => item.id === uuid) ?? null;
      setCurrentNotification(find);
      setOpen(true);
    }
  };

  const getNotificationsReadUnread = useCallback(
    async () => {
      try {
        return await service.getNotificationsByUser();
      } catch (error) {
        const errorMessageRead = "Erro ao buscar notificações do portal";
        enqueueSnackbar(errorMessageRead, { variant: "error" });
        return [];
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service]
  );

  const markCurrentAsRead = async () => {
    if (!currentNotification) return;

    try {
      await service.readNotification(currentNotification.id);
    } catch (error) {
      const errorMessageRead = "Erro ao buscar notificações do portal";
      enqueueSnackbar(errorMessageRead, { variant: "error" });
    }
  };

  const markCurrentAsUnread = async () => {
    if (!currentNotification) return;

    try {
      await service.unreadNotification(currentNotification.id);
    } catch (error) {
      const errorMessageRead = "Erro ao buscar notificações do portal";
      enqueueSnackbar(errorMessageRead, { variant: "error" });
    }
  };

  const handleNext = async () => {
    if (!currentNotification) return;

    // 1) marca a atual como lida no backend
    await markCurrentAsRead();

    // 2) busca novamente a lista já atualizada (lido/não lido)
    const active = await getNotificationsReadUnread();

    if (active.length > 0) {
      setNotifications(active);

      // 3) encontra a próxima não lida (qualquer uma)
      const nextNotification = active.find((item) => !item.readAt) ?? null;

      if (nextNotification) {
        setCurrentNotification(nextNotification);
        setOpen(true);
        return;
      }
    }

    // 4) se não tiver próxima não lida, fecha o modal
    setCurrentNotification(null);
    setOpen(false);
  };

  const handleUnread = async () => {
    if (!currentNotification) return;

    // 1) marca a atual como não lida no backend
    await markCurrentAsUnread();

    // 2) busca novamente a lista já atualizada (lido/não lido)
    const active = await getNotificationsReadUnread();

    if (active.length > 0) {
      setNotifications(active);

      // 3) encontra a próxima não lida (qualquer uma)
      const nextNotification = active.find((item) => !item.readAt) ?? null;

      if (nextNotification) {
        setCurrentNotification(nextNotification);
        setOpen(true);
        return;
      }
    }
  };

  const messageReadCount = useCallback(() => {
    return notifications?.filter((item) => !item?.readAt).length;
  }, [notifications]);

  const autoFetchGetNotificationsReadUnread = useCallback(async () => {
    const active = await getNotificationsReadUnread();

    if (active.length > 0) {
      setNotifications(active);

      const firstUnread = active.find((n) => !n.readAt) ?? null;
      setCurrentNotification(firstUnread);
    } else {
      setNotifications([]);
      setCurrentNotification(null);
      setOpen(false);
    }
  }, [getNotificationsReadUnread]);

  return {
    state: {
      open,
      notifications,
      currentNotification,
      hasNext: hasNextNotification,
      hasPortalNotification: notifications.length > 0,
      messageReadCount: messageReadCount(),
    },
    action: {
      handleClose,
      handleNext,
      handleUnread,
      reopen,
      autoFetchGetNotificationsReadUnread,
    },
  };
};

export default useNotificationPortalInternal;
